import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Text } from './Texte';
import { BrandLogo } from './BrandLogo';
import { Ionicons } from './icons';
import { tabs, type TabIcon } from './BottomNav';
import type { TabKey } from '../types';
import { t } from '../i18n';
import { interfaceRtl } from '../i18n/sens';
import { colors, gradients, layout, radius, spacing, typography } from '../theme/tokens';

export interface BarreLateraleProps {
  /** `etendu` : barre de 252 px avec libellés (ordinateur). `moyen` : rail d'icônes (tablette). */
  variante: 'etendu' | 'moyen';
  /** `null` : aucun onglet (écran posé au-dessus des onglets, comme le Coaching). */
  activeKey: TabKey | null;
  onTabPress: (key: TabKey) => void;
  /** Ouvre la page Coaching : c'est le rôle du bouton étincelle de la barre du téléphone. */
  onCoachingPress: () => void;
  testID?: string;
}

/**
 * NAVIGATION DES GRANDS ÉCRANS : le pendant de `BottomNav`, jamais rendue avec elle.
 *
 * Mêmes destinations, même ordre (Carrière en tête, donc route d'atterrissage), mêmes identifiants
 * de test (`nav-cvs`, `nav-fab`…) : seule la forme change. Le bouton étincelle du téléphone, qui
 * ouvre le Coaching, devient une action nommée en bas de la barre : sur un grand écran on a la
 * place d'écrire ce qu'il fait.
 *
 * Le filet de séparation passe du côté du contenu : à droite, ou à gauche en arabe (la barre est
 * alors posée à droite par `TabNavigator`).
 */
export function BarreLaterale({ variante, activeKey, onTabPress, onCoachingPress, testID }: BarreLateraleProps) {
  const etendu = variante === 'etendu';
  const rtl = interfaceRtl();
  const onglets = tabs();

  return (
    <View
      testID={testID}
      style={[
        styles.barre,
        etendu ? styles.barreEtendue : styles.barreRail,
        rtl ? styles.filetGauche : styles.filetDroit,
      ]}
    >
      <View style={[styles.logo, !etendu && styles.logoRail]}>
        {etendu ? <BrandLogo variant="wordmark" width={LARGEUR_LOGO} /> : <BrandLogo variant="mark" size={RAIL_MARQUE} />}
      </View>

      <View style={styles.liste}>
        {onglets.map((onglet) => (
          <Entree
            key={onglet.key}
            testID={`nav-${onglet.key}`}
            libelleTestID={`nav-${onglet.key}-label`}
            libelle={onglet.label}
            icone={onglet.icon}
            actif={onglet.key === activeKey}
            etendu={etendu}
            onPress={() => onTabPress(onglet.key)}
          />
        ))}
      </View>

      <Pressable
        testID="nav-fab"
        accessibilityRole="button"
        accessibilityLabel={t('coaching.title')}
        onPress={onCoachingPress}
        style={etendu ? styles.coachingEtendu : styles.coachingRail}
      >
        <LinearGradient
          testID="nav-fab-gradient"
          colors={gradients.brand}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={etendu ? styles.coachingFondEtendu : styles.coachingFondRail}
        >
          <Ionicons name="sparkles" size={etendu ? 20 : 24} color={colors.white} />
          {etendu ? <Text style={styles.coachingLibelle}>{t('coaching.title')}</Text> : null}
        </LinearGradient>
      </Pressable>
    </View>
  );
}

function Entree({
  testID,
  libelleTestID,
  libelle,
  icone,
  actif,
  etendu,
  onPress,
}: {
  testID: string;
  libelleTestID: string;
  libelle: string;
  icone: TabIcon;
  actif: boolean;
  etendu: boolean;
  onPress: () => void;
}) {
  const couleur = actif ? colors.electricDeep : colors.grey500;
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityState={{ selected: actif }}
      onPress={onPress}
      style={[etendu ? styles.entreeEtendue : styles.entreeRail, actif && styles.entreeActive]}
    >
      <Ionicons name={icone} size={etendu ? 22 : 24} color={couleur} />
      <Text
        testID={libelleTestID}
        style={[etendu ? styles.libelleEtendu : styles.libelleRail, { color: couleur }, actif && styles.libelleActif]}
        numberOfLines={1}
      >
        {libelle}
      </Text>
    </Pressable>
  );
}

const LARGEUR_LOGO = 150;
const RAIL_MARQUE = 40;

const styles = StyleSheet.create({
  barre: {
    backgroundColor: colors.white,
    paddingVertical: spacing.xl,
    borderColor: colors.grey200,
  },
  barreEtendue: { width: layout.barreLateraleLargeur, paddingHorizontal: spacing.lg },
  barreRail: { width: layout.railLargeur, paddingHorizontal: spacing.sm, alignItems: 'center' },
  filetDroit: { borderRightWidth: 1 },
  filetGauche: { borderLeftWidth: 1 },
  logo: { paddingHorizontal: spacing.sm, marginBottom: spacing.xl },
  logoRail: { paddingHorizontal: 0, alignItems: 'center' },
  liste: { gap: spacing.xs, flex: 1, width: '100%' },
  entreeEtendue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    borderRadius: radius.md,
  },
  entreeRail: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    borderRadius: radius.md,
    width: '100%',
  },
  entreeActive: { backgroundColor: colors.blueSoft },
  libelleEtendu: { fontSize: typography.bodyMd.size, fontWeight: typography.bodyMd.weight },
  libelleRail: { fontSize: typography.caption.size, fontWeight: typography.caption.weight },
  libelleActif: { fontWeight: '700' },
  coachingEtendu: { borderRadius: radius.lg, overflow: 'hidden' },
  coachingFondEtendu: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    borderRadius: radius.lg,
  },
  coachingLibelle: { color: colors.white, fontSize: typography.bodyMd.size, fontWeight: '700' },
  coachingRail: { borderRadius: radius.pill, overflow: 'hidden' },
  coachingFondRail: {
    width: 52,
    height: 52,
    borderRadius: radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
